import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Rocket, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

interface HookTrackWizardFooterProps {
  currentStep: number;
  totalSteps: number;
  canGoNext: boolean;
  onBack: () => void;
  onNext: () => void;
  onFinish?: () => void;
  isFinishing?: boolean;
  onExit?: () => void;
  // Props customizáveis para textos
  finishButtonText?: string;
  finishingButtonText?: string;
  stepTexts?: Record<number, string>;
  tooltipTexts?: Record<number, string>;
  // Para wizards com steps 1-indexed (como HookTrack)
  stepsStartAtOne?: boolean;
}

// Textos dinâmicos por etapa (defaults para Hook Test)
const getNextButtonText = (
  currentStep: number,
  isFinalStep: boolean,
  finishButtonText?: string,
  stepTexts?: Record<number, string>
) => {
  if (isFinalStep) return finishButtonText || translateInterfaceText("Iniciar Teste");
  if (stepTexts && stepTexts[currentStep]) return stepTexts[currentStep];
  switch (currentStep) {
    case 1:
      return translateInterfaceText("Avançar para Intros em Teste");
    case 2:
      return translateInterfaceText("Configurar Distribuição");
    case 3:
      return translateInterfaceText("Configurar Rastreamento");
    default:
      return translateInterfaceText("Continuar");
  }
};

const getTooltipText = (currentStep: number, tooltipTexts?: Record<number, string>) => {
  if (tooltipTexts && tooltipTexts[currentStep]) return tooltipTexts[currentStep];
  switch (currentStep) {
    case 1:
      return translateInterfaceText("Selecione um vídeo base para continuar");
    case 2:
      return translateInterfaceText("Selecione pelo menos uma intro para continuar");
    case 3:
      return translateInterfaceText("Configure a distribuição de tráfego para continuar");
    case 4:
      return translateInterfaceText("Selecione a plataforma e informe o token, ou desative o rastreamento");
    default:
      return translateInterfaceText("Complete esta etapa para continuar");
  }
};

export function HookTrackWizardFooter({
  currentStep,
  totalSteps,
  canGoNext,
  onBack,
  onNext,
  onFinish,
  isFinishing,
  onExit,
  finishButtonText,
  finishingButtonText,
  stepTexts,
  tooltipTexts,
  stepsStartAtOne = false
}: HookTrackWizardFooterProps) {
  useLanguage();
  const [showWarning, setShowWarning] = useState(false);

  const isFinalStep = stepsStartAtOne
    ? currentStep === totalSteps
    : currentStep === totalSteps - 1;
  const canGoBack = currentStep > 0;
  const nextButtonText = getNextButtonText(currentStep, isFinalStep, finishButtonText, stepTexts);
  const tooltipText = getTooltipText(currentStep, tooltipTexts);

  const handleFinishClick = () => {
    setShowWarning(true);
  };

  const handleConfirm = () => {
    setShowWarning(false);
    onFinish?.();
  };

  const NextButton = ({ className, fullWidth = false }: { className?: string; fullWidth?: boolean }) => { useLanguage(); return ((
    <Button
      onClick={isFinalStep ? handleFinishClick : onNext}
      disabled={!canGoNext || isFinishing}
      className={cn(
        fullWidth ? (canGoBack ? "w-1/2" : "w-full") : "min-w-[200px]",
        "h-[44px] px-6 bg-[#1D4FFF] hover:bg-[#1A47E5] active:bg-[#1640D0] text-white font-semibold rounded-[10px] gap-2 transition-all duration-200",
        className
      )}
    >
    {isFinishing ? (
        finishingButtonText || translateInterfaceText("Criando...")
      ) : (
        <>
          {isFinalStep && <Rocket className="h-5 w-5" />}
          <span className={cn(isFinalStep ? "" : "hidden sm:inline")}>{nextButtonText}</span>
          {!isFinalStep && <span className="sm:hidden">{translateInterfaceText("Continuar")}</span>}
          {!isFinalStep && <ChevronRight className="h-4 w-4" />}
        </>
      )}
    </Button>
  )); };

  return (
    <>
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent className="w-auto !max-w-[480px] p-4">
        <AlertDialogHeader className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            </div>
            <AlertDialogTitle className="text-[15px] font-semibold text-[#1A1A1A]">
              {translateInterfaceText("Atenção antes de criar o teste")}</AlertDialogTitle>
          </div>
          <div className="hidden lg:block h-px w-full my-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />
          <AlertDialogDescription className="text-[13px] text-[#4B5563] leading-relaxed space-y-2">
            <p>{translateInterfaceText("O teste usará as")}{" "}<strong className="text-[#1A1A1A]">{translateInterfaceText("configurações de personalização atuais")}</strong> {" "}{translateInterfaceText("do vídeo.")}</p>
            <p>{translateInterfaceText("Alterações futuras no vídeo")}{" "}<strong className="text-[#1A1A1A]">{translateInterfaceText("não serão refletidas")}</strong> {" "}{translateInterfaceText("nas variações já criadas.")}</p>
            <p>{translateInterfaceText("Para melhores resultados, use vídeos com a")}{" "}<strong className="text-[#1A1A1A]">{translateInterfaceText("mesma proporção")}</strong> {" "}{translateInterfaceText("em todas as variações, evitando diferenças no tamanho do player.")}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel className="h-9 px-4 text-[13px] font-medium hover:text-[#1A1A1A] hover:bg-[#f0f0f0]">
            {translateInterfaceText("Cancelar")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="h-9 px-4 text-[13px] font-semibold bg-[#1D4FFF] hover:bg-[#1A47E5] text-white"
          >
            <Rocket className="w-3.5 h-3.5 mr-1.5" />
            {translateInterfaceText("Criar teste")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <div
      className="fixed bottom-0 left-0 right-0 lg:left-[250px] z-50 bg-white border-t border-[#E4E7EC] shadow-[0_-4px_20px_rgba(0,0,0,0.04)]"
      style={{
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        minHeight: '72px'
      }}
    >
      <div className="w-full max-w-[1440px] mx-auto px-4 lg:px-16 pt-3 sm:pt-4">
        {/* Mobile: Horizontal Buttons PRD */}
        <div className="flex flex-row gap-3 lg:hidden" style={{ minHeight: '44px' }}>
          {canGoBack && (
            <Button
              variant="outline"
              onClick={onBack}
              className="w-1/2 h-[44px] px-4 bg-white border border-[#E4E7EC] text-[#6B7280] font-medium rounded-[10px]"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              {translateInterfaceText("Voltar")}</Button>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(canGoBack ? "w-1/2" : "w-full")}>
                  <NextButton fullWidth />
                </span>
              </TooltipTrigger>
              {!canGoNext && (
                <TooltipContent side="top" className="bg-[#1A1A1A] text-white text-[13px] px-3 py-2 rounded-lg">
                  <p>{tooltipText}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Desktop: Original Horizontal Layout */}
        <div className="hidden lg:flex items-center justify-between gap-4">
          {/* Left Side: Exit Button */}
          <div className="flex items-center gap-2">
            {onExit && (
              <Button
                variant="ghost"
                onClick={onExit}
                className="text-[#6B7280] hover:text-[#EF4444] hover:bg-[#FEF2F2] font-medium"
              >
                {translateInterfaceText("Sair")}</Button>
            )}
          </div>

          {/* Center: Progress Indicator */}
          <div className="hidden md:flex items-center gap-2 text-sm text-[#6B7280]">
            <span className="font-medium">
              {translateInterfaceText("Etapa")}{" "}{currentStep} {" "}{translateInterfaceText("de")}{" "}{totalSteps}
            </span>
          </div>

          {/* Right Side: Back + Next/Finish Buttons PRD */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={!canGoBack}
              className="h-[44px] px-6 bg-white border border-[#E4E7EC] text-[#6B7280] font-medium rounded-[10px] gap-2 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              {translateInterfaceText("Voltar")}</Button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <NextButton />
                  </span>
                </TooltipTrigger>
                {!canGoNext && (
                  <TooltipContent side="top" className="bg-[#1A1A1A] text-white text-[13px] px-3 py-2 rounded-lg">
                    <p>{tooltipText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
